"use client";

import { CheckCircle } from "@semo/icons";
import { cn } from "../../utils";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "../../primitives/tooltip";

/**
 * VendorBadge Props
 * Determined which badge variant to display and its styling
 */
export interface VendorBadgeProps {
  variant: "lowest" | "highestTrust" | "trustPercentile" | "contracted";
  trustPercentile?: number;
  className?: string;
}

const BADGE_CONFIG = {
  lowest: {
    label: "💰 최저가",
    tooltip:
      "최저가가 항상 정답은 아닐 수도 있어요. 내게 필요한 조건이 맞는지 같이 체크해보면 좋아요 🙂",
    className: "bg-green-500 shadow-sm shadow-green-500/20 border-green-600",
    ariaLabel: "최저가 업체",
  },
  highestTrust: {
    label: "⭐ 최고신뢰도",
    tooltip:
      "신뢰도란, 20년 공공 조달 실적을 AI로 분석한 점수 입니다. 처음 보는 업체라도, 신뢰도를 보면 판단이 쉬워져요 🙂",
    className: "bg-orange-500 shadow-sm shadow-orange-500/20 border-orange-600",
    ariaLabel: "최고 신뢰도 업체",
  },
  trustPercentile: {
    label: (percentile: number) =>
      percentile <= 1 ? "상위1%" : `상위 ${percentile}%`,
    tooltip:
      "신뢰도란, 20년 공공 조달 실적을 AI로 분석한 점수 입니다. 처음 보는 업체라도, 신뢰도를 보면 판단이 쉬워져요 🙂",
    className: "",
    ariaLabel: "신뢰도 퍼센타일",
  },
  contracted: {
    label: "계약됨",
    tooltip: "이미 계약이 완료된 견적입니다",
    className: "bg-gray-900 shadow-sm border-gray-950",
    ariaLabel: "계약 완료",
  },
} as const;

function getTrustPercentileClassName(percentile: number): string {
  if (percentile <= 1)
    return "bg-amber-500 shadow-sm shadow-amber-500/20 border-amber-600";
  if (percentile <= 5)
    return "bg-orange-500 shadow-sm shadow-orange-500/20 border-orange-600";
  if (percentile <= 10)
    return "bg-orange-400 shadow-sm shadow-orange-500/10 border-orange-500";
  if (percentile <= 20)
    return "bg-primary shadow-sm shadow-primary/20 border-primary-dark";
  return "bg-gray-400 shadow-sm border-gray-500";
}

export function VendorBadge({
  variant,
  trustPercentile,
  className,
}: VendorBadgeProps) {
  const config = BADGE_CONFIG[variant];
  const label =
    typeof config.label === "function"
      ? config.label(trustPercentile ?? 0)
      : config.label;
  const badgeClassName =
    variant === "trustPercentile" && trustPercentile !== undefined
      ? getTrustPercentileClassName(trustPercentile)
      : config.className;

  const badgeContent = (
    <div
      role="status"
      aria-label={config.ariaLabel}
      className={cn(
        "px-2.5 py-1 rounded-[6px] text-2xs font-semibold text-white border",
        "inline-flex items-center gap-1",
        variant === "lowest" || variant === "highestTrust" ? "cursor-help" : "",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50",
        badgeClassName,
        className,
      )}
      tabIndex={variant === "lowest" || variant === "highestTrust" ? 0 : -1}
    >
      {variant === "contracted" && <CheckCircle className="w-3 h-3 shrink-0" />}
      <span className="leading-none uppercase tracking-wider">{label}</span>
    </div>
  );

  if (variant === "lowest" || variant === "highestTrust") {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{badgeContent}</TooltipTrigger>
        <TooltipContent
          className="max-w-[280px] text-sm leading-relaxed font-semibold bg-gray-900 text-white border-none p-3 rounded-[12px]"
          side="top"
          sideOffset={8}
        >
          {config.tooltip}
        </TooltipContent>
      </Tooltip>
    );
  }

  return badgeContent;
}
